import React, { useEffect, useState, useMemo } from "react";
import { Layout, Menu, Avatar, Dropdown, Space, Button, Tooltip, Select, message, Modal, Badge, notification } from "antd";
import { useNavigate, useLocation, useOutlet } from "react-router-dom";
import KeepAliveTab from "../common/KeepAliveTab";
import sseService from "../../services/sseService";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  AppstoreOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  GlobalOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  BellOutlined,
  GiftOutlined,
  SwapOutlined,
  DashboardOutlined,
  BarChartOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import NotificationCenter from "../common/NotificationCenter";

import { setCookie, getCookie } from "../../utils/cookie";
import locale from "@aligov/global-locale";

import * as authService from "../../services/auth/authService";
import { getUnreadNotificationCount } from "../../services/notification/notificationService";
import { navigateToLogin } from "../../utils/navigation";

const { Header, Sider, Content } = Layout;

// 语言标映射
const langMap = {
  // "zh-CN": "zh",
  "en-US": "en",
  "th-TH": "th",
};

// 前端隐藏的菜单路径（不修改后端数据，仅在渲染时过滤）
const HIDDEN_MENU_PATHS = ["/role"];

// 递归过滤隐藏菜单项
const filterHiddenMenuItems = (items) => {
  if (!Array.isArray(items)) return items;
  return items
    .filter((item) => !HIDDEN_MENU_PATHS.includes(item.path) && !HIDDEN_MENU_PATHS.includes(item.key))
    .map((item) => ({
      ...item,
      children: item.children ? filterHiddenMenuItems(item.children) : item.children,
    }));
};

// 创建带 Tooltip 的菜单项标签
const MenuItemWithTooltip = ({ label, collapsed, isLeaf = true }) => {
  // 检查文本是否可能被截断
  const shouldShowTooltip = label && (label.length > 15 || collapsed);

  if (shouldShowTooltip) {
    return (
      <Tooltip title={label} placement={collapsed ? "right" : "top"}>
        <span className="menu-item-with-tooltip">{label}</span>
      </Tooltip>
    );
  }

  return <span className="menu-item-with-tooltip">{label}</span>;
};

const MainLayout = () => {
  // 菜单默认展开
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [menuItemsData, setMenuItemsData] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [rawMenuData, setRawMenuData] = useState([]);

  const titleKeyMap = useMemo(() => {
    const map = new Map();
    const traverse = (items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        const displayText = item.title || item.label || item.name;
        if (item.key && displayText) {
          map.set(item.key, displayText);
        }
        if (item.path && displayText && !map.has(item.path)) {
          map.set(item.path, displayText);
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          traverse(item.children);
        }
      });
    };
    traverse(rawMenuData);

    // 非菜单页面的 fallback 标题（如通过用户菜单、按钮等入口打开的页面）
    const nonMenuPages = {
      '/profile': t('user.profile'),
      '/settings': t('user.settings'),
      '/403': t('forbidden.title'),
      '/404': t('notFound.title'),
    };
    Object.entries(nonMenuPages).forEach(([path, title]) => {
      if (!map.has(path)) {
        map.set(path, title);
      }
    });

    return map;
  }, [rawMenuData, t]);

  // 初始化：设置多语言、获取服务端菜单、获取仓库列表和未读通知
  useEffect(() => {
    const { lang } = locale.getLocale();
    if (lang) {
      i18n.changeLanguage(langMap[lang] || lang);
    } else {
      i18n.changeLanguage("th-TH");
    }

    if (isAuthenticated) {
      const initMenu = async () => {
        try {
          const menuRes = await authService.getMenu();
          if (!menuRes?.data?.length) {
            navigate("/403");
            return;
          }
          const menuData = filterHiddenMenuItems((menuRes.data || []).sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0)));
          setRawMenuData(menuData);
          setMenuItemsData(processMenuItems(menuData, collapsed));
        } catch (error) {
          console.error('Failed to fetch menu:', error);
        }
      };
      initMenu();
      fetchWarehouseList();
      fetchUnreadCount();
    }
  }, [isAuthenticated]);

  // 订阅 SSE notification 事件：收到实时通知时弹出提示并刷新未读数
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleNotification = (data) => {
      const payload = data || {};
      const title = payload.title || t("notification.center.title");
      const content = payload.content || "";

      notification.open({
        message: title,
        description: content,
        placement: "topRight",
        duration: 4.5,
        icon: <BellOutlined style={{ color: "#1677ff" }} />,
        onClick: () => {
          setNotificationVisible(true);
          notification.destroy();
        },
      });

      // 重新拉取最新未读数，保证与服务端一致
      fetchUnreadCount();
    };

    sseService.on("notification", handleNotification);
    return () => {
      sseService.off("notification", handleNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // 获取未读通知数量
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadNotificationCount();
      if (response.success && response.data !== undefined) {
        setUnreadCount(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // 处理未读数量变化
  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  // collapsed 变化时重新处理菜单项（Tooltip 行为依赖 collapsed）
  useEffect(() => {
    if (rawMenuData.length > 0) {
      setMenuItemsData(processMenuItems(rawMenuData, collapsed));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  // 获取仓库列表和当前仓库
  const fetchWarehouseList = async () => {
    try {
      const response = await authService.getUserInfo();
      const userData = response.data;
      
      // 设置当前仓库
      if (userData.userContext.warehouseId && userData.userContext.warehouseId != -1) {
        setCurrentWarehouse({
          id: userData.userContext.warehouseId,
          name: userData.userContext.warehouseName,
          timeZone: userData.userContext.warehouseTimeZone,
        });
        setSelectedWarehouse(userData.userContext.warehouseId);
      }
      
      // 设置仓库列表选项
      if (userData.warehouseList && userData.warehouseList.length > 0) {
        const options = userData.warehouseList.map(warehouse => ({
          value: warehouse.id,
          label: `${warehouse.name}-${warehouse.code}`,
          timeZone: warehouse.timeZone,
          code: warehouse.code,
        }));
        setWarehouseList(options);
      }
    } catch (error) {
      message.error(t('switchWarehouse.fetchUserInfoFailed'));
    }
  };

  const handleLogout = () => {
    logout();
    navigateToLogin();
  };

  const handleLanguageChange = (lang, key) => {
    i18n.changeLanguage(lang);
    locale.setLang(key);

    // 重新加载刷新菜单
    window.location.reload();
    // setCookie("x-hng", `lang=${key}`);
  };

  // 处理仓库选择变化
  const handleWarehouseChange = (value) => {
    setSelectedWarehouse(value);
  };

  // 确认切换仓库
  const handleSwitchWarehouse = () => {
    if (!selectedWarehouse || selectedWarehouse === currentWarehouse?.id) {
      return;
    }

    Modal.confirm({
      title: t('switchWarehouse.title'),
      content: t('switchWarehouse.confirm'),
      onOk: async () => {
        try {
          setSwitchLoading(true);
          await authService.updateCurrentWarehouse(selectedWarehouse);
          message.success(t('switchWarehouse.switchSuccess'));
          
          // 刷新页面以更新仓库信息
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error) {
          message.error(t('switchWarehouse.switchFailed'));
        } finally {
          setSwitchLoading(false);
        }
      },
    });
  };

  // 图标名称映射
  const iconMap = {
    AppstoreOutlined: <AppstoreOutlined />,
    DashboardOutlined: <DashboardOutlined />,
    GlobalOutlined: <GlobalOutlined />,
    BarChartOutlined: <BarChartOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    ShopOutlined: <ShopOutlined />,
    SwapOutlined: <SwapOutlined />,
    TeamOutlined: <TeamOutlined />,
    SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  };

  // 处理菜单项，为每个标签添加 Tooltip
  const processMenuItems = (items, collapsed) => {
    return items.map((item) => {
      const processedItem = {
        ...item,
        title: item?.title ?? item?.label ?? item?.name ?? "",
        icon: iconMap[item.icon] || <AppstoreOutlined />,
      };

      // 判断是否为叶子节点（没有children或children为空数组）
      const isLeaf = !item.children || item.children.length === 0;

      // 为标签添加 Tooltip，优先使用 title，其次 label，最后 name
      const displayText = item.title || item.label || item.name;
      if (displayText) {
        processedItem.label = (
          <MenuItemWithTooltip
            label={displayText}
            collapsed={collapsed}
            isLeaf={isLeaf}
          />
        );
      }

      // 递归处理子菜单项
      if (item.children) {
        processedItem.children = processMenuItems(item.children, collapsed);
      }

      return processedItem;
    });
  };

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("user.profile"),
      onClick: () => navigate("/profile"),
    },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: t("user.settings"),
    //   onClick: () => navigate("/settings"),
    // },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("user.logout"),
      onClick: handleLogout,
    },
  ];

  const languageMenuItems = [
    {
      key: "en-US",
      label: "English",
      onClick: () => handleLanguageChange("en", "en-US"),
    },
    {
      key: "th-TH",
      label: "ไทย",
      onClick: () => handleLanguageChange("th", "th-TH"),
    },
    // {
    //   key: "zh-CN",
    //   label: "中文",
    //   onClick: () => handleLanguageChange("zh", "zh-CN"),
    // },
  ];

  // 通知菜单项（已移除，改为弹窗）

  return (
    <Layout className="main-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        className="adaptive-sider"
      >
        <div style={{ padding: "16px", textAlign: "center", color: "white" }}>
          <h3 style={{ color: "white", margin: 0 }}>{t("login.title")}</h3>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={menuItemsData.map((item) => item.key)} // 默认展开所有一级菜单
          items={menuItemsData}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: "0 16px",
            background: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 1px 4px rgba(0,21,41,.08)",
            height: 40,
            lineHeight: "40px",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: "16px", width: 40, height: 40 }}
          />

          <Space size="middle">
            {/* 仓库切换 Select */}
            {warehouseList.length > 0 && (
              <Space.Compact>
                <Select
                  value={selectedWarehouse}
                  onChange={handleWarehouseChange}
                  style={{ minWidth: 150 }}
                  showSearch
                  placeholder={t('switchWarehouse.targetWarehousePlaceholder')}
                  optionFilterProp="label"
                  options={warehouseList}
                  suffixIcon={<ShopOutlined />}
                />
                <Button 
                  type="primary" 
                  onClick={handleSwitchWarehouse}
                  loading={switchLoading}
                  disabled={!selectedWarehouse || selectedWarehouse === currentWarehouse?.id}
                >
                  {t('switchWarehouse.confirm')}
                </Button>
              </Space.Compact>
            )}

            {/* 通知入口 - 放在 Language 左边，点击打开通知中心弹窗 */}
            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
              <Button
                type="text"
                icon={<BellOutlined />}
                onClick={() => setNotificationVisible(true)}
              />
            </Badge>

            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomRight"
            >
              <Button type="text" icon={<GlobalOutlined />}>
                {t("common.language")}
              </Button>
            </Dropdown>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="user-avatar">
                <Avatar
                  src={user?.avatar}
                  icon={<UserOutlined />}
                  size="small"
                />
                {!collapsed && (
                  <span className="user-info" style={{ marginLeft: 8 }}>
                    {user?.name === '管理员' ? 'Admin' : user?.name}
                  </span>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>
        <Content className="site-layout-content">
          <KeepAliveTab
            titleKeyMap={titleKeyMap}
            outlet={useOutlet()}
            location={location}
            onChange={(path) => navigate(path)}
          />
        </Content>
      </Layout>
      {/* 通知中心弹窗 */}
      <NotificationCenter
        visible={notificationVisible}
        onClose={() => setNotificationVisible(false)}
        onUnreadCountChange={handleUnreadCountChange}
      />
    </Layout>
  );
};

export default MainLayout;
